import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../../contexts/AuthContext';

// Define types for active users and cursor positions
interface ActiveUser {
  userId: string;
  username: string;
  cursorPosition?: {
    sectionId: number;
    position: number;
  };
}

interface CursorUpdate {
  userId: string;
  username: string;
  sectionId: number;
  position: number;
}

interface SectionUpdate {
  sectionId: number;
  content: string;
  userId: string;
  username: string;
  timestamp: Date;
}

// Define the collaboration context type
interface CollaborationContextType {
  connected: boolean;
  activeUsers: ActiveUser[];
  cursorPositions: Record<string, CursorUpdate>;
  joinDocument: (documentId: string) => void;
  leaveDocument: () => void;
  updateCursorPosition: (sectionId: number, position: number) => void;
  updateSectionContent: (sectionId: number, content: string) => void;
  documentLocked: boolean;
  lockDocument: () => Promise<boolean>;
  unlockDocument: () => Promise<boolean>;
  lockedBy: string | null;
  lockExpiresAt: Date | null;
}

// Create the context
const CollaborationContext = createContext<CollaborationContextType | undefined>(undefined);

// Define the provider props
interface CollaborationProviderProps {
  children: ReactNode;
}

// Create the provider component
export const CollaborationProvider: React.FC<CollaborationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [cursorPositions, setCursorPositions] = useState<Record<string, CursorUpdate>>({});
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null);
  const [documentLocked, setDocumentLocked] = useState(false);
  const [lockedBy, setLockedBy] = useState<string | null>(null);
  const [lockExpiresAt, setLockExpiresAt] = useState<Date | null>(null);

  // Initialize socket connection
  useEffect(() => {
    // Create socket connection
    const socketInstance = io(process.env.REACT_APP_API_URL || 'http://localhost:5000', {
      withCredentials: true,
    });

    // Set up event listeners
    socketInstance.on('connect', () => {
      console.log('Connected to server');
      setConnected(true);
    });

    socketInstance.on('disconnect', () => {
      console.log('Disconnected from server');
      setConnected(false);
    });

    socketInstance.on('user-joined', (data: { 
      userId: string, 
      username: string, 
      activeUsers: ActiveUser[] 
    }) => {
      setActiveUsers(data.activeUsers || []);
    });

    socketInstance.on('user-left', (data: { 
      userId: string, 
      socketId: string, 
      activeUsers: ActiveUser[] 
    }) => {
      setActiveUsers(data.activeUsers || []);
      
      // Remove cursor position for user who left
      setCursorPositions(prev => {
        const newPositions = { ...prev };
        delete newPositions[data.userId];
        return newPositions;
      });
    });

    socketInstance.on('cursor-updated', (data: CursorUpdate) => {
      setCursorPositions(prev => ({
        ...prev,
        [data.userId]: data
      }));
    });

    socketInstance.on('section-updated', (data: SectionUpdate) => {
      // Notify parent component about section update
      document.dispatchEvent(new CustomEvent('section-updated', {
        detail: data
      }));
    });

    socketInstance.on('document-locked', (data: {
      userId: string,
      username: string,
      expiresAt: string
    }) => {
      setDocumentLocked(true);
      setLockedBy(data.username);
      setLockExpiresAt(new Date(data.expiresAt));
    });

    socketInstance.on('document-unlocked', () => {
      setDocumentLocked(false);
      setLockedBy(null);
      setLockExpiresAt(null);
    });

    // Store the socket instance
    setSocket(socketInstance);

    // Clean up on unmount
    return () => {
      if (currentDocumentId) {
        socketInstance.emit('leave-document', currentDocumentId);
      }
      socketInstance.disconnect();
    };
  }, []);

  // Join a document for collaborative editing
  const joinDocument = (documentId: string) => {
    if (!socket || !user) return;

    // Leave current document if any
    if (currentDocumentId) {
      socket.emit('leave-document', currentDocumentId);
    }

    // Join new document
    socket.emit('join-document', {
      documentId,
      userId: user.id,
      username: user.name || user.email
    });

    setCurrentDocumentId(documentId);
    setDocumentLocked(false);
    setLockedBy(null);
    setLockExpiresAt(null);
  };

  // Leave the current document
  const leaveDocument = () => {
    if (!socket || !currentDocumentId) return;

    socket.emit('leave-document', currentDocumentId);
    setCurrentDocumentId(null);
    setActiveUsers([]);
    setCursorPositions({});
    setDocumentLocked(false);
    setLockedBy(null);
    setLockExpiresAt(null);
  };

  // Update cursor position
  const updateCursorPosition = (sectionId: number, position: number) => {
    if (!socket || !user || !currentDocumentId) return;

    socket.emit('cursor-position', {
      documentId: currentDocumentId,
      sectionId,
      position,
      userId: user.id,
      username: user.name || user.email
    });
  };

  // Update section content
  const updateSectionContent = (sectionId: number, content: string) => {
    if (!socket || !user || !currentDocumentId) return;

    socket.emit('section-change', {
      documentId: currentDocumentId,
      sectionId,
      content,
      userId: user.id,
      username: user.name || user.email
    });
  };

  // Lock document for editing
  const lockDocument = async (): Promise<boolean> => {
    if (!currentDocumentId || !user) return false;

    try {
      const response = await fetch(`/api/documents/${currentDocumentId}/lock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ durationMinutes: 5 }),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDocumentLocked(true);
        setLockedBy(user.name || user.email);
        setLockExpiresAt(new Date(data.lockedUntil));
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error locking document:', error);
      return false;
    }
  };

  // Unlock document
  const unlockDocument = async (): Promise<boolean> => {
    if (!currentDocumentId) return false;

    try {
      const response = await fetch(`/api/documents/${currentDocumentId}/unlock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (response.ok) {
        setDocumentLocked(false);
        setLockedBy(null);
        setLockExpiresAt(null);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error unlocking document:', error);
      return false;
    }
  };

  // Provide the collaboration context
  const value = {
    connected,
    activeUsers,
    cursorPositions,
    joinDocument,
    leaveDocument,
    updateCursorPosition,
    updateSectionContent,
    documentLocked,
    lockDocument,
    unlockDocument,
    lockedBy,
    lockExpiresAt
  };

  return (
    <CollaborationContext.Provider value={value}>
      {children}
    </CollaborationContext.Provider>
  );
};

// Create a hook for using the collaboration context
export const useCollaboration = (): CollaborationContextType => {
  const context = useContext(CollaborationContext);
  if (context === undefined) {
    throw new Error('useCollaboration must be used within a CollaborationProvider');
  }
  return context;
}; 